import type { ChartConfig, ChartData } from '@indutrust/chart-model';
import type { EChartsOption } from 'echarts';
import type { ChartOptionBuildOptions } from './chart-option-chrome';
import {
  buildAxisCommon,
  buildSharedChrome,
  buildStableGrid,
  buildTooltipStyle,
  hexToRgba,
  pickColor,
  resolvePalette,
} from './chart-option-chrome';
import { resolveEchartsThemeTokens } from './resolve-echarts-theme-tokens';
import type { SupportedChartConfiguration } from './supported-chart-configuration';

/**
 * Builds the shared ECharts option bag for cartesian charts (line, bar, area):
 * category x-axis from `data.labels`, one series per dataset colored from
 * the palette, and an axis-scoped tooltip.
 */
function buildCartesianOptions(
  type: 'bar' | 'line',
  data: ChartData,
  config: ChartConfig,
  buildOptions: ChartOptionBuildOptions = {},
  extras: {
    area?: boolean;
    stack?: string;
    horizontal?: boolean;
  } = {}
): EChartsOption {
  const theme = buildOptions.theme ?? resolveEchartsThemeTokens();
  const palette = resolvePalette(config, theme);
  const axis = buildAxisCommon(theme);
  const categoryAxis = {
    type: 'category' as const,
    data: data.labels,
    name: extras.horizontal ? config.yAxisLabel : config.xAxisLabel,
    ...axis,
  };
  const valueAxis = {
    type: 'value' as const,
    name: extras.horizontal ? config.xAxisLabel : config.yAxisLabel,
    ...axis,
  };

  return {
    ...buildSharedChrome(theme, buildOptions.showToolbox === true),
    tooltip: {
      ...buildTooltipStyle(theme),
      trigger: 'axis',
    },
    legend: {
      show: true,
      bottom: 0,
      textStyle: { color: theme.mutedColor },
    },
    // Fixed insets — never containLabel / outerBounds (see buildStableGrid).
    grid: extras.horizontal
      ? buildStableGrid({ left: 140, right: 24, top: 24, bottom: 24 })
      : buildStableGrid(),
    xAxis: extras.horizontal ? valueAxis : categoryAxis,
    yAxis: extras.horizontal ? categoryAxis : valueAxis,
    series: data.datasets.map((dataset, index) => {
      const color = pickColor(palette, index);
      const base = {
        name: dataset.name,
        type,
        data: dataset.data,
        itemStyle: {
          color,
          ...(type === 'bar'
            ? { borderRadius: extras.horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0] }
            : {}),
        },
        ...(extras.stack ? { stack: extras.stack } : {}),
      };

      if (type === 'line') {
        return {
          ...base,
          smooth: true,
          lineStyle: { color, width: 2.5 },
          symbol: 'circle',
          symbolSize: 6,
          ...(extras.area
            ? {
                areaStyle: {
                  color: {
                    type: 'linear',
                    x: 0,
                    y: 0,
                    x2: 0,
                    y2: 1,
                    colorStops: [
                      { offset: 0, color: hexToRgba(color, 0.35) },
                      { offset: 1, color: hexToRgba(color, 0.02) },
                    ],
                  },
                },
              }
            : {}),
        };
      }

      return base;
    }),
  };
}

/**
 * Generate an ECharts configuration for a line chart.
 *
 * @param data The input series and labels. {@link ChartData}
 * @param config Chart configuration (axis labels, colors, etc.). {@link ChartConfig}
 * @param buildOptions Optional theme / toolbox overrides.
 * @returns A {@link SupportedChartConfiguration} ready to feed `idt-chart-renderer`.
 * @example
 * const opt = createLineChartOptions(data, { type: 'line', xAxisLabel: 'Month', yAxisLabel: 'Sales' });
 */
export function createLineChartOptions(
  data: ChartData,
  config: ChartConfig,
  buildOptions: ChartOptionBuildOptions = {}
): SupportedChartConfiguration {
  return {
    type: 'line',
    options: buildCartesianOptions('line', data, config, buildOptions),
  };
}

/**
 * Generate an ECharts configuration for a smooth area chart (line + gradient fill).
 *
 * @param data The input series and labels. {@link ChartData}
 * @param config Chart configuration. {@link ChartConfig}
 * @param buildOptions Optional theme / toolbox overrides.
 * @returns A {@link SupportedChartConfiguration} with `type: 'area'`.
 * @example
 * const opt = createAreaChartOptions(data, { type: 'area' });
 */
export function createAreaChartOptions(
  data: ChartData,
  config: ChartConfig,
  buildOptions: ChartOptionBuildOptions = {}
): SupportedChartConfiguration {
  return {
    type: 'area',
    options: buildCartesianOptions('line', data, config, buildOptions, {
      area: true,
    }),
  };
}

/**
 * Generate an ECharts configuration for a bar chart.
 *
 * @param data The input series and labels. {@link ChartData}
 * @param config Chart configuration (axis labels, colors, etc.). {@link ChartConfig}
 * @param buildOptions Optional theme / toolbox overrides.
 * @returns A {@link SupportedChartConfiguration} ready to feed `idt-chart-renderer`.
 * @example
 * const opt = createBarChartOptions(data, { type: 'bar', xAxisLabel: 'Category', yAxisLabel: 'Count' });
 */
export function createBarChartOptions(
  data: ChartData,
  config: ChartConfig,
  buildOptions: ChartOptionBuildOptions = {}
): SupportedChartConfiguration {
  return {
    type: 'bar',
    options: buildCartesianOptions('bar', data, config, buildOptions),
  };
}

/**
 * Generate an ECharts configuration for a stacked bar chart.
 *
 * @param data The input series and labels. {@link ChartData}
 * @param config Chart configuration. {@link ChartConfig}
 * @param buildOptions Optional theme / toolbox overrides.
 * @returns A {@link SupportedChartConfiguration} with `type: 'stacked-bar'`.
 * @example
 * const opt = createStackedBarChartOptions(data, { type: 'stacked-bar' });
 */
export function createStackedBarChartOptions(
  data: ChartData,
  config: ChartConfig,
  buildOptions: ChartOptionBuildOptions = {}
): SupportedChartConfiguration {
  return {
    type: 'stacked-bar',
    options: buildCartesianOptions('bar', data, config, buildOptions, {
      stack: 'total',
    }),
  };
}

/**
 * Generate an ECharts configuration for a horizontal bar chart.
 *
 * @param data The input series and labels. {@link ChartData}
 * @param config Chart configuration. {@link ChartConfig}
 * @param buildOptions Optional theme / toolbox overrides.
 * @returns A {@link SupportedChartConfiguration} with `type: 'horizontal-bar'`.
 * @example
 * const opt = createHorizontalBarChartOptions(data, { type: 'horizontal-bar' });
 */
export function createHorizontalBarChartOptions(
  data: ChartData,
  config: ChartConfig,
  buildOptions: ChartOptionBuildOptions = {}
): SupportedChartConfiguration {
  return {
    type: 'horizontal-bar',
    options: buildCartesianOptions('bar', data, config, buildOptions, {
      horizontal: true,
    }),
  };
}
